import React from 'react';

import WebformNumberBase from '@uncinc/react-drupal-core/dist/Components/Webform/WebformNumber';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import { useTranslation } from 'react-i18next';

import FormRange from '../../Form/Range';

interface WebformNumberField extends Record<string, unknown> {
  '#admin_title': string
  '#default_value': string
  '#max': number | null
  '#min': number | null
  '#required': boolean
  '#step': number | null
  '#title': string
  '#type': 'number'
  '#visible': boolean
  '#webform': string
  '#webform_children': unknown[]
  '#webform_composite': boolean
  '#webform_depth': number
  '#webform_id': string
  '#webform_key': string
  '#webform_multiple': boolean
  '#webform_parent_flexbox': boolean
  '#webform_parent_key': string
  '#webform_parents': string[]
  '#webform_plugin_id': 'number'
}

interface WebformNumberProps extends Record<string, unknown> {
  entity: ImmutableEntity<WebformNumberField>;
  touched: Record<string, unknown>
  errors: Record<string, unknown>
  values: Record<string, unknown>
}

const WebformNumberSlider: React.FC<WebformNumberProps> = ({
  entity,
  customClasses,
  touched,
  errors,
  values,
  entities,
  info,
  ...restProps
}) => {
  const { t } = useTranslation();
  console.debug(entity.toJS());

  const title = entity.get('#title');
  const description = entity.get('#description') as string | undefined;
  const key = entity.get('#webform_key');
  const required = entity.get('#required');
  const requiredError = entity.get('#required_error');

  const prefix = entity.get('#field_prefix', '') as string;
  const suffix = entity.get('#field_suffix', '') as string;
  const min = entity.get('#min', 0) as number;
  const max = entity.get('#max', 100) as number;
  const step = entity.get('#step', 10) as number;
  const defaultValue = Number.parseInt((entity.get('#default', '50') as string), 10);

  return (
    <div className="WebformNumberSlider">
      <FormRange
        type="range"
        {...restProps}
        title={title}
        required={required}
        prefix={prefix}
        suffix={suffix}
        min={min}
        max={max}
        step={step}
        defaultValue={defaultValue}
        description={description}
        fieldName={key}
        validation={(value: unknown) => {
          let error;
          if (required && !value) {
            error = requiredError || t(['webform.components.number.required', 'webform.required', 'webform_required']);
          }
          return error;
        }}
        touched={touched[key]}
        error={errors[key]}
        value={values[key]}
        style={{
          '--min': 0,
          '--max': 100,
        }}
      />
      <output />
      <div className="range-slider__progress" />
    </div>
  );
};

const WebformNumber: React.FC<WebformNumberProps> = ({ entity, ...props }) => {
  if (entity.get('#input_type') === 'range') {
    return <WebformNumberSlider entity={entity} {...props} />;
  }

  return <WebformNumberBase entity={entity} {...props} />;
};

export default WebformNumber;
